import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import PaymentCallback from './pages/PaymentCallback';
import SecondHand from './pages/secondhand/SecondHand';
import SecondHandDetail from './pages/secondhand/SecondHandDetail';
import SellItem from './pages/secondhand/SellItem';
import Blog from './pages/blog/Blog';
import BlogPost from './pages/blog/BlogPost';
import AdminBlog from './pages/blog/AdminBlog';

// Protected route wrapper
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();
  if (loading) return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  if (!user) return <Navigate to="/signin" state={{ from: location }} replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  return children;
};

// Layout with Navbar+Footer (not for admin)
const StoreLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
  </>
);

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <CartProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { background: '#1a1a1a', color: '#fff', border: '1px solid #3a3a3a', fontSize: '14px' },
            success: { iconTheme: { primary: '#e2001a', secondary: '#fff' } },
          }}
        />
        <Routes>
          {/* Store routes */}
          <Route path="/" element={<StoreLayout><Home /></StoreLayout>} />
          <Route path="/products" element={<StoreLayout><Products /></StoreLayout>} />
          <Route path="/products/:slug" element={<StoreLayout><ProductDetail /></StoreLayout>} />
          <Route path="/second-hand" element={<StoreLayout><SecondHand /></StoreLayout>} />
          <Route path="/second-hand/sell" element={<StoreLayout><ProtectedRoute><SellItem /></ProtectedRoute></StoreLayout>} />
          <Route path="/second-hand/:slug" element={<StoreLayout><SecondHandDetail /></StoreLayout>} />
          <Route path="/second-hand/:slug/edit" element={<StoreLayout><ProtectedRoute><SellItem /></ProtectedRoute></StoreLayout>} />
          <Route path="/cart" element={<StoreLayout><Cart /></StoreLayout>} />
          <Route path="/checkout" element={<StoreLayout><ProtectedRoute><Checkout /></ProtectedRoute></StoreLayout>} />
          <Route path="/dashboard" element={<StoreLayout><ProtectedRoute><Dashboard /></ProtectedRoute></StoreLayout>} />
          {/* Where Khalti / Fonepay send the payer back to */}
          <Route path="/payment/callback" element={<StoreLayout><ProtectedRoute><PaymentCallback /></ProtectedRoute></StoreLayout>} />
          <Route path="/signin" element={<StoreLayout><SignIn /></StoreLayout>} />
          <Route path="/signup" element={<StoreLayout><SignUp /></StoreLayout>} />

          {/* Admin — no footer/navbar, full panel */}
          <Route path="/blog" element={<StoreLayout><Blog /></StoreLayout>} />
          <Route path="/blog/:slug" element={<StoreLayout><BlogPost /></StoreLayout>} />
          <Route path="/admin/*" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
          <Route path="/admin/blog" element={<ProtectedRoute adminOnly><AdminBlog /></ProtectedRoute>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </CartProvider>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
